import { FC, useMemo } from 'react';
import { numberToCurrency } from 'helpers/currency';
import cn from 'classnames';
import { CardInfo } from 'components/cards';
import { CartValuesProps } from '.';
import s from './cart-values.module.css';

const CartValues: FC<CartValuesProps> = ({
  coupon,
  shipping = [],
  cartTotal,
  total,
  className,
  row = false,
  totalItems,
  onPage,
  selectedDeliveryKeys,
  hasOnlyExpItems = false,
}) => {

  function adjustShippingOptions(shippingOptions: any, selectedDeliveryKeys: any) {
    const sellerToDeliveryMethod: any = {};
    selectedDeliveryKeys.forEach((key: any) => {
      const [sellerSlug, deliveryMethod] = key.split('/');
      sellerToDeliveryMethod[sellerSlug] = deliveryMethod;
    });

    const adjustedShippingOptions: any = [];
    const processedSellers = new Set(); // Mantém o controle dos sellers já processados

    shippingOptions.forEach((option: any) => {
      const adjustedOption = { ...option };

      if (Array.isArray(option.deliveryKey)) {
        // Frete agrupado (por exemplo, Jadlog)
        const deliveryMethod = 'fazendinhalog'; // Ou 'jadlog', dependendo do seu caso

        // Filtra sellers cujo método selecionado corresponde ao frete agrupado
        adjustedOption.sellers = [];
        adjustedOption.deliveryKey = [];

        option.deliveryKey.forEach((sellerSlug: string, index: number) => {
          const sellerName = option.sellers[index];

          if (
            sellerToDeliveryMethod[sellerSlug] === deliveryMethod &&
            !processedSellers.has(sellerSlug)
          ) {
            adjustedOption.sellers.push(sellerName);
            adjustedOption.deliveryKey.push(sellerSlug);
            processedSellers.add(sellerSlug); // Marca o seller como processado
          }
        });

        if (adjustedOption.sellers.length > 0) {
          adjustedShippingOptions.push(adjustedOption);
        }
        // Se não houver sellers restantes na opção, não inclui essa opção de frete
      } else {
        // Frete individual ou FazendinhaLog com múltiplos slugs ("slug1,slug2/method")
        const deliveryKey = option.deliveryKey;
        const lastSlash = deliveryKey.lastIndexOf('/');
        const slugsPart = deliveryKey.substring(0, lastSlash);
        const method = deliveryKey.substring(lastSlash + 1);
        const slugs = slugsPart.split(',');
        const primarySlug = slugs[0];

        if (
          sellerToDeliveryMethod[primarySlug] === method &&
          !processedSellers.has(primarySlug)
        ) {
          adjustedShippingOptions.push(adjustedOption);
          slugs.forEach((s: string) => processedSellers.add(s));
        }
      }
    });

    return adjustedShippingOptions;
  }

  const adjustedShippingOptions = useMemo(() => {
    return adjustShippingOptions(shipping, selectedDeliveryKeys);
  }, [shipping, selectedDeliveryKeys]);

  const computedShippingTotal = useMemo(() => {
    let shippingTotal = 0;
    if (adjustedShippingOptions && adjustedShippingOptions.length > 0) {
      adjustedShippingOptions.forEach((sp: any) => {
        if (sp !== undefined) shippingTotal += sp.value;
      });
    }

    return shippingTotal;
  }, [adjustedShippingOptions]);

  let cart;
  if (typeof window !== 'undefined') {
    cart = localStorage.getItem('cart');
  } else {
    cart = null;
  }

  // @ts-ignore
  const convertedCart =  JSON.parse(cart);

  let newTotal = cartTotal + computedShippingTotal;
  // @ts-ignore
  let onlySubs = convertedCart?.items?.filter((item: any) => item.assinatura === true) || [];
  // @ts-ignore
  if (onlySubs.length === convertedCart?.items?.length) {
    newTotal = cartTotal;
  }

  if (row) {
    return (
      <div className={cn(s.root, className)}>
        <div className={s.row}>
          <div className={s.label}>Produtos ({totalItems})</div>
          <div className={s.value}>
            <span>R$</span> {numberToCurrency(cartTotal)}
          </div>
        </div>
        {coupon && (
          <div className={s.row}>
            <div className={s.label}>{coupon.description}</div>
            <div className={s.value}>
              <span>R$</span> {numberToCurrency(coupon.value)}
            </div>
          </div>
        )}
        {!hasOnlyExpItems && onPage !== 'cart' &&
          adjustedShippingOptions &&
          adjustedShippingOptions.length > 0 &&
          adjustedShippingOptions.map(
            (sp: any) =>
              sp !== undefined && (
                <div className={s.row} key={Array.isArray(sp.deliveryKey) ? sp.deliveryKey.join(',') : sp.deliveryKey}>
                  <div className={s.label}>
                    <small>{sp.sellers && sp.sellers.join(', ')}</small>
                    <br />
                    <strong>{sp.description}</strong>
                    {sp.frequency && (
                      <>
                        <br />
                        Entrega {sp.frequency}
                      </>
                    )}
                  </div>
                  <div className={s.value}>
                    {sp.freeShipping ? (
                      <div className={s.free}>Frete Grátis</div>
                    ) : (
                      <>
                        <span>R$</span> {numberToCurrency(sp.value)}
                      </>
                    )}
                  </div>
                </div>
              ),
          )}
        <div className={s.row}>
          <div className={cn(s.label, s.total)}>Total</div>
          <div className={cn(s.value, s.total)}>
            <span>R$</span> {onPage === "checkout" ? numberToCurrency(newTotal) : numberToCurrency(cartTotal)}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={cn(s.cards, className)}>
      <CardInfo title="Subtotal" value={numberToCurrency(cartTotal)} />
      {adjustedShippingOptions && <CardInfo title="Frete" value={numberToCurrency(computedShippingTotal)} />}
      {coupon && <CardInfo title={coupon.description} value={numberToCurrency(coupon.value)} />}
      <CardInfo title="Total" value={numberToCurrency(newTotal)} />
    </div>
  );
};

export default CartValues;
